import * as React from 'react';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import { FormControl, FormGroup, FormLabel, Popover, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import TaskApiService from '../../api/TaskApiService';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { DataGridPro } from '@mui/x-data-grid-pro';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDeleteDialog from './ConfirmDelete';
import PropTypes from 'prop-types';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import SaveButton from '../Common/SaveButton';
import { useAsyncCallback } from '../../hooks/useAsyncCallback';
import { useSnackbar } from '../Common/SnackbarProvider';

const taskApiService = new TaskApiService();

const SIBLING_TASK = 0;
const CHILD_TASK = 1;
const PARENT_TASK = 2;

export default function TaskRelationshipModal (props) {
    const { onClose, open, taskId, ...other } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const [relatedTaskId, setRelatedTaskId] = useState('');
    const [description, setDescription] = useState('');

    /**
     * @type Array<TaskRelationshipDto>
     */
    const [relatedTasks, setRelatedTasks] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedDirection, setSelectedDirection] = useState('');
    const [selectedRelationship, setSelectedRelationship] = useState();
    const [anchorEl, setAnchorEl] = useState(null);
    const [otherTaskDescription, setOtherTaskDescription] = useState();
    const [openPopover, setOpenPopover] = useState(false);

    async function handlePopoverOpen (event) {
        setAnchorEl(event.currentTarget);
        const result = await taskApiService.getTaskDescription(event.currentTarget.getAttribute('task-id'));
        setOpenPopover(true);
        setOtherTaskDescription(result.data);
    }

    function handlePopoverClose () {
        setOtherTaskDescription('');
        setOpenPopover(false);
        setAnchorEl(null);
    }

    function getRelationshipFromInt (direction) {
        if (direction === SIBLING_TASK) {
            return t('taskRelationship.relationship.sibling');
        } else if (direction === CHILD_TASK) {
            return t('taskRelationship.relationship.child');
        } else if (direction === PARENT_TASK) {
            return t('taskRelationship.relationship.parent');
        }
    }

    const columns = useMemo(() => [
        {
            field: 'baseTask',
            headerName: t('taskRelationship.column.baseTask'),
            flex: 1,
            renderCell: (params) => {
                return <Link
                    underline='always'
                    target='_blank'
                    href={'/Task/' + params.row.baseTask}
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}
                    task-id={params.row.baseTask}
                >
                    {params.row.baseTask}
                </Link>;
            },
        },
        {
            field: 'relatedTask',
            headerName: t('taskRelationship.column.relatedTask'),
            flex: 1,
            renderCell: (params) => {
                return <Link
                    underline='always'
                    target='_blank'
                    href={'/Task/' + params.row.relatedTask}
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}
                    task-id={params.row.relatedTask}
                >
                    {params.row.relatedTask}
                </Link>;
            },
        },
        {
            field: 'description',
            headerName: t('fieldName.description'),
            minWidth: 110,
            flex: 2,
        },
        {
            field: 'direction',
            headerName: t('taskRelationship.column.type'),
            flex: 1,
            renderCell: (params) => {
                return <label>{getRelationshipFromInt(params.row.direction)}</label>;
            },
        },
        {
            field: 'delete',
            headerName: '',
            renderCell: (params) => {
                return <IconButton
                    aria-label={t('common.deleteAriaLabel')}
                    onClick={() => {
                        setShowDeleteDialog(true);
                        setSelectedRelationship(params.row);
                    }
                    }
                >
                    <DeleteIcon />
                </IconButton>;
            },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [t]);

    // The related-task field and the direction radios both hand back strings, but the
    // API binds them to ints -- posting the raw strings is rejected before the action runs.
    const relatedTaskIdNumber = parseInt(relatedTaskId, 10);
    const canSave = !isNaN(relatedTaskIdNumber) && relatedTaskIdNumber > 0
        && relatedTaskIdNumber !== taskId && selectedDirection !== '';

    const { run: saveNewRelationship, loading: saving } = useAsyncCallback(async () => {
        const newRelationship = {
            baseTask: taskId,
            relatedTask: relatedTaskIdNumber,
            description,
            direction: parseInt(selectedDirection, 10),
        };
        const response = await taskApiService.addRelatedTask(newRelationship);
        if (response.success) {
            snackbar.success(t('taskRelationship.toast.created'));
            resetModalValues();
            await GetUpdateTaskRelationships();
        } else {
            snackbar.error(t('taskRelationship.toast.createError', { error: response.message }));
        }
    });

    function resetModalValues () {
        setRelatedTaskId('');
        setSelectedDirection('');
        setDescription('');
    }

    const confirmDelete = async (value) => {
        if (value) {
            const deletedRelationshipResult = await taskApiService.deleteRelatedTask(selectedRelationship);
            if (deletedRelationshipResult.success) {
                snackbar.success(t('taskRelationship.toast.deleted'));
                await GetUpdateTaskRelationships();
            } else {
                snackbar.error(t('taskRelationship.toast.deleteError', { error: deletedRelationshipResult.message }));
            }
        }
        setShowDeleteDialog(false);
    };

    const handleCancel = () => {
        resetModalValues();
        onClose(false);
    };

    const GetUpdateTaskRelationships = async () => {
        const relationshipsResponse = await taskApiService.getRelatedTasks(taskId);
        if (relationshipsResponse.success) {
            setRelatedTasks(relationshipsResponse.data);
        } else {
            snackbar.error(t('taskRelationship.toast.retrieveError', { error: relationshipsResponse.message }));
        }
    };

    useEffect(() => {
        if(open) {
            async function getData () {
                await GetUpdateTaskRelationships();
            }
            void getData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Dialog
            sx={{ '& .MuiDialog-paper': { width: 1000, maxHeight: 1000 } }}
            maxWidth="md"
            open={open}
            {...other}
        >
            <DialogTitle>{t('taskRelationship.title')}</DialogTitle>
            <DialogContent
                dividers
                style={{ width: '100%' }}
            >
                <FormGroup>
                    <FormControl>
                        <FormLabel>{t('taskRelationship.field.relatedTaskId')}</FormLabel>
                        <TextField
                            value={relatedTaskId}
                            onChange={(event) => {
                                setRelatedTaskId(event.target.value);
                            }}
                        />
                    </FormControl>

                </FormGroup>
                <FormGroup>
                    <FormControl>
                        <FormLabel>{t('fieldName.description')}</FormLabel>
                        <TextField
                            value={description}
                            onChange={(event) => {
                                setDescription(event.target.value);
                            }}
                        />
                    </FormControl>

                </FormGroup>
                <RadioGroup
                    value={selectedDirection}
                    onChange={(event) => setSelectedDirection(event.target.value)}
                >
                    <FormControlLabel control={<Radio />} label={t('taskRelationship.radio.sibling')} value={0}/>
                    <FormControlLabel control={<Radio />} label={t('taskRelationship.radio.child')} value={1}/>
                    <FormControlLabel control={<Radio />} label={t('taskRelationship.radio.parent')} value={2}/>
                </RadioGroup>

                <SaveButton
                    loading={saving}
                    disabled={!canSave}
                    onClick={saveNewRelationship}
                    variant='contained'
                >
                    {t('taskRelationship.button.saveNew')}
                </SaveButton>
                <br/><br/>
                <div style={{ height: 300, width: '100%' }}>
                    <DataGridPro
                        columns={columns}
                        rows={relatedTasks}
                        getRowId={(row) => row.id}
                    />
                </div>

            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handleCancel} variant='contained' disabled={saving}>
                    {t('common.close')}
                </Button>
            </DialogActions>
            <ConfirmDeleteDialog onClose={confirmDelete} open={showDeleteDialog} />
            <Popover
                open={openPopover}
                sx={{
                    pointerEvents: 'none',
                }}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
            >
                <Typography sx={{ p: 1 }}>{otherTaskDescription}</Typography>
            </Popover>
        </Dialog>
    );
}

TaskRelationshipModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    taskId: PropTypes.number.isRequired,
};
